import * as React from 'react'
import { t } from 'ttag'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  CustomType,
  Gender,
  GetUserDailyDiaryProgress$Request,
  Lang,
  ProgressType,
  Units,
  UserDailyDiaryProgress,
  UserProfileDto,
} from '../../services/api.types'
import { Card } from '../../components/Card/Card'
import {
  Bar,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryStringOrNumberCallback,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory'
// @ts-ignore
import * as d3Scale from 'd3-scale'

import dayjs from '../../dayjsConfig'
import './Progress.scss'
import { Grid } from '@material-ui/core'
import VictoryTheme from '../../styles/victory-theme'
import YumProgressPeriod, { ProgressPeriod } from '../../components/YumProgressPeriod/YumProgressPeriod'
import { formatThousands } from '../../common/helpers/translations'
import Hidden from '@material-ui/core/Hidden'
import Loader from '../../components/Loader/Loader'
import YumTabs from '../../components/YumTabs/YumTabs'
import YumTab from '../../components/YumTabs/YumTab/YumTab'
import ProgressChips from './components/ProgressChips'
import { getActivityIconComponent } from '../../common/workoutActivities'
import {
  unitsCountDurationHourShort,
  unitsCountDurationMinuteShort,
  unitsCountEnergyKilocalorieShort,
  unitsCountLengthKilometerShort,
  unitsCountLengthMileShort,
  unitsDisplayDurationCalorieLong,
  unitsDisplayDurationMinuteLong,
} from '../../common/helpers/units'
import { currentLang, currentLocale, isRtl } from '../../i18nInit'
import YumInsufficientPermissionsWrapper from '../../components/YumInsufficientPermissions/YumInsufficientPermissionsWrapper'
import YumLogTable from '../../components/YumCustomTable/YumLogTable'
import { getUnit } from '../../services/CustomTypes'
import { darkerGray, darkGreen, primaryBlue } from '../../styles/material-ui-theme-v2'
import { getUserCompanies } from '../RegisterUser/store/RegisterUserSelectors'
import { store } from '../../index'
import { IGlobalState } from '../../Root/store/appReducer'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'
import YumGrid from '../../components/YumGrid/YumGrid'
import { useOsScale } from '../../common/helpers/useOsScale'

const CLOSE_TICK_HIDE_PERCENTAGE = 25

export interface IProgressProps {
  dailyDiary: UserDailyDiaryProgress[]
  progressType: ProgressType
  customType: CustomType
  progressPeriod: ProgressPeriod
  userDto: UserProfileDto

  setProgressType(payload: ProgressType): void
  setProgressCustomType(payload: CustomType): void
  setProgressPeriod(payload: ProgressPeriod): void
  setOnboardingStatusToActivityData(): void
  getDailyProgress(payload: GetUserDailyDiaryProgress$Request): void
}

export const convertMinutesToHoursAndMinutes = (totalMinutes: number): string => {
  if (totalMinutes <= 0) return `0${unitsCountDurationMinuteShort}`
  const hr = Math.floor(totalMinutes / 60)
  const min = totalMinutes % 60
  return `${hr}${unitsCountDurationHourShort} ${min}${unitsCountDurationMinuteShort}`
}

export const convertMinutesToHours = (totalMinutes: number): string => {
  if (totalMinutes <= 0) return `0${unitsCountDurationHourShort}`
  const hr = Math.floor(totalMinutes / 60)
  return `${hr}${unitsCountDurationHourShort}`
}

export const getProgressType = (progressType: ProgressType, customType: CustomType) => {
  if (progressType === ProgressType.FORMULA) return CustomType.ACTIVITY_INDEX
  if (progressType === ProgressType.DISTANCE || progressType === ProgressType.INTENSIVE_MINUTES) {
    return customType
  }
  return progressType as unknown as CustomType
}

const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout)
      timeout = null
    }
    timeout = setTimeout(() => func(...args), waitFor)
  }

  return debounced as (...args: Parameters<F>) => ReturnType<F>
}

const convertNumbers2English = function (string: any) {
  return string
    .replace(/[\u0660-\u0669]/g, function (c: any) {
      return c.charCodeAt(0) - 0x0660
    })
    .replace(/[\u06f0-\u06f9]/g, function (c: any) {
      return c.charCodeAt(0) - 0x06f0
    })
}

const convertNumbers2Arabic = function (string: any) {
  return String(string).replace(/[0-9]/g, function (c: any) {
    return '٠١٢٣٤٥٦٧٨٩'.charAt(c)
  })
}

const getTickValues = (goalValue: number | null, maxDomainValue: number) => {
  const ticks = d3Scale.scaleLinear().domain([0, maxDomainValue]).ticks(4)
  if (goalValue !== null && !ticks.includes(goalValue)) {
    ticks.push(goalValue)
    // We don't want to sort, so the goal will be rendered last and will be the upmost layer.
    //ticks.sort()
  }
  return ticks
}

const getFormattedDates = (startDate: Date, endDate: Date) => {
  let dates = 'N/A'
  if (startDate && endDate) {
    // Local time zone — the period boundaries come from YumProgressPeriod as
    // local instants, so formatting them as UTC would shift the shown dates.
    const dateTimeFormat = new Intl.DateTimeFormat(currentLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    // @ts-ignore
    dates = dateTimeFormat.formatRange(startDate, endDate)
  }
  return dates
}

const getMaxDataValue = (data: any) =>
  Math.max(
    ...data.map(function (d: any) {
      return d['sum']
    }),
  ) * 1.1

const getAge = (dateOfBirth: string) => {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
  return age
}

const PASSIVE_TO_TOTAL_CALORIES_MULTIPLIER = 1.55
const FEMALE_CALORIES_CONSTANT = -161
const MALE_CALORIES_CONSTANT = 5
const getUserCaloriesGoal = (userDto: UserProfileDto) => {
  let goal = 2500
  if (userDto) {
    const { weight, height, dateOfBirth, gender } = userDto
    if (weight && height && dateOfBirth && gender) {
      const age = getAge(dateOfBirth)
      const passiveCalories =
        10 * weight +
        6.25 * (height / 100) * 100 -
        5 * age +
        (gender === Gender.MALE ? MALE_CALORIES_CONSTANT : FEMALE_CALORIES_CONSTANT)
      goal = Math.round(PASSIVE_TO_TOTAL_CALORIES_MULTIPLIER * passiveCalories)
    }
  }
  return goal
}

const getGoalValue = (
  progressType: ProgressType,
  customType: CustomType,
  userDto: UserProfileDto,
  showMiles?: boolean,
  customStepsGoal?: number | null,
) => {
  switch (progressType) {
    case ProgressType.STEPS:
      return customStepsGoal || 12000
    case ProgressType.SLEEP_MIN:
      return 420
    case ProgressType.DISTANCE:
      switch (customType) {
        case CustomType.CYCLING_KM:
          return showMiles ? 10 : 16
        case CustomType.SWIMMING_M:
          return 1000
        default:
          return showMiles ? 6 : 8
      }
    case ProgressType.CALORIES:
      return getUserCaloriesGoal(userDto)
    case ProgressType.INTENSIVE_MINUTES:
      return 20
    case ProgressType.FORMULA:
      return 100
    default:
      return 1
  }
}

const getMaxDomainValue = (progressType: ProgressType, data: any, maxGoalValue: number | null) => {
  const maxDataValue = getMaxDataValue(data)
  let maxDomainValue = maxGoalValue
    ? maxGoalValue * 2
    : progressType === ProgressType.FORMULA
      ? 200
      : progressType === ProgressType.CALORIES
        ? 3000
        : progressType === ProgressType.STEPS
          ? 20000
          : 40
  if (maxDataValue > maxDomainValue) maxDomainValue = maxDataValue
  return maxDomainValue
}

const processDailyDiary = (
  dailyDiary: UserDailyDiaryProgress[],
  startDate: Date,
  endDate: Date,
): UserDailyDiaryProgress[] => {
  if (!dailyDiary || !dailyDiary.length) return []
  const startDay = dayjs(startDate).locale('en').format('YYYY-MM-DD')
  const endDay = dayjs(endDate).locale('en').format('YYYY-MM-DD')
  const firstDate = dailyDiary[0].date
  const lastDate = dailyDiary[dailyDiary.length - 1].date
  if (firstDate === startDay && lastDate === endDay) return dailyDiary
  return []
}

// Leading chip icon for an activity-type filter, reusing the activity-card icon of the type's
// representative normalized value (same look as the Activities view filters).
const typeFilterIcon = (normalizedType: string): React.ReactElement | undefined => {
  const IconComponent = getActivityIconComponent(normalizedType)
  return IconComponent ? <IconComponent /> : undefined
}

export default function Progress(props: IProgressProps) {
  const { userDto, progressType, customType, progressPeriod, dailyDiary } = props
  const hasActivityIndex = !!userDto.dateOfBirth

  /*const { serviceProvider: serviceProviderRaw } = userDto
  const distanceScopeMissing =
    serviceProviderRaw === ServiceProvider.GOOGLE_FIT &&
    userIntegrationScopes != '' &&
    !userIntegrationScopes.includes('https://www.googleapis.com/auth/fitness.location.read')
  const distanceIntegrationUrl = `${ROUTES.INTEGRATION_PROXY.to}?type=${serviceProviderRaw}&scope=LEAN&d=1`
  const serviceProvider = getServiceProviderLabel(serviceProviderRaw)*/

  /*const handleGoogleFitUnverified = async () => {
    const { value: consent } = await Dialog.confirm({
      title: 'Google Fit',
      message: `Google has not verified our app yet for syncing distance data. You will soon see a screen stating it. There you need to click "Advanced" and "Go to YuMuuv (unsafe)" to continue.`,
      okButtonTitle: t`OK`,
      cancelButtonTitle: t`Cancel`,
    })
    if (!consent) return
    store.dispatch(push(distanceIntegrationUrl))
  }*/

  const showMiles = userDto.units === Units.IMPERIAL
  const now = new Date()
  const [startDate, setStartDate] = React.useState(now)
  const [endDate, setEndDate] = React.useState(now)
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth,
  })

  // Please change Katriito kliinik company progress tab goal from 12k steps a day to 6k steps a day
  const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
  const isKatriito =
    userCompanies && userCompanies.some(({ companyId }) => companyId === 'b671988d-b6e3-4fac-b737-7b0bdffbaaf8')
  const syncSleep = hasFeatureFlag(userCompanies, 'sleepSync')
  const hideProgressGoals = hasFeatureFlag(userCompanies, 'hideProgressGoals')
  const exerciseMinutesOnly = hasFeatureFlag(userCompanies, 'exerciseMinutesOnlyProgressView')

  const isSleep = progressType === ProgressType.SLEEP_MIN
  const isMobile = dimensions.width < 576
  const isTablet = dimensions.width < 992
  // Victory renders SVG <text> with numeric `fontSize` in user units; CSS rem
  // doesn't apply there, so scale chart text manually with the OS scale.
  const osScale = useOsScale()
  const chartFontSize = (isMobile ? 13 : 5) * osScale
  const chartRightPadding = isMobile ? 45 : isTablet ? 25 : 20
  const chartLeftPadding = isMobile ? 10 : 5

  const chartStrokeWidth = isMobile ? 2 : 1
  const chartTooltipFontSize = (isMobile ? 10 : 5) * osScale
  const chartTooltipStrokeWidth = isMobile ? 1.5 : 0.75

  const chartTooltipCornerRadius = isMobile ? 4 : 2
  const chartTooltipPointerWidth = isMobile ? 8 : 4
  const chartTooltipPointerLength = isMobile ? 7 : 3.5
  const chartTooltipFlyoutPadding = isMobile
    ? { top: 3.5, bottom: 3.5, left: 6, right: 6 }
    : { top: 1.75, bottom: 1.75, left: 3, right: 3 }
  const chartTooltipDy = isMobile ? -8 : -4

  const syncDailyProgress = (startDate: Date, endDate: Date) => {
    props.getDailyProgress({
      startDate: dayjs(startDate).locale('en').format('YYYY-MM-DD'),
      endDate: dayjs(endDate).locale('en').format('YYYY-MM-DD'),
      customType: getProgressType(progressType, customType),
    })
  }

  // Refresh view after front-end sync.
  React.useEffect(() => {
    if (startDate !== endDate) {
      syncDailyProgress(startDate, endDate)
    }
  }, [progressType, customType, userDto.lastSync])

  React.useEffect(() => {
    if (!exerciseMinutesOnly) return
    if (progressType !== ProgressType.INTENSIVE_MINUTES) {
      props.setProgressType(ProgressType.INTENSIVE_MINUTES)
    }
    if (customType === CustomType.INTENSIVE_MINUTES || customType === CustomType.MINDFULNESS_MIN) {
      props.setProgressCustomType(CustomType.EXERCISE_MIN)
    }
  }, [exerciseMinutesOnly, progressType, customType])

  React.useEffect(() => {
    if (userDto.units === Units.IMPERIAL) {
      if (
        [CustomType.DISTANCE_KM, CustomType.WALKING_KM, CustomType.RUNNING_KM, CustomType.CYCLING_KM].includes(
          customType,
        )
      ) {
        props.setProgressCustomType(CustomType.DISTANCE_MI)
      }
    } else {
      if (
        [CustomType.DISTANCE_MI, CustomType.WALKING_MI, CustomType.RUNNING_MI, CustomType.CYCLING_MI].includes(
          customType,
        )
      ) {
        props.setProgressCustomType(CustomType.DISTANCE_KM)
      }
    }
  }, [userDto.units])

  React.useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      })
    }, 400)

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  })

  const onProgressTypeChange = (event: React.ChangeEvent<unknown>, progressType: ProgressType) => {
    props.setProgressType(progressType)
    if (progressType === ProgressType.DISTANCE) {
      props.setProgressCustomType(showMiles ? CustomType.DISTANCE_MI : CustomType.DISTANCE_KM)
    } else if (progressType === ProgressType.INTENSIVE_MINUTES) {
      props.setProgressCustomType(CustomType.EXERCISE_MIN)
    }
  }

  const setProgressPeriodDates = (
    progressPeriod: ProgressPeriod,
    progressPeriodDates: { startDate: Date; endDate: Date },
  ) => {
    const { startDate, endDate } = progressPeriodDates
    setStartDate(startDate)
    setEndDate(endDate)

    props.setProgressPeriod(progressPeriod)
    syncDailyProgress(startDate, endDate)
  }

  const periodDays = dayjs(endDate).diff(dayjs(startDate), 'days') + 1

  const [data, setData] = React.useState([] as UserDailyDiaryProgress[])

  React.useEffect(() => {
    const diary = processDailyDiary(dailyDiary, startDate, endDate)
    setData(diary ? (isRtl ? diary.slice(0).reverse() : diary) : [])
  }, [dailyDiary])

  /*const progressValueIndicator =
    progressType === ProgressType.STEPS
      ? 'stepsTotal'
      : progressType === ProgressType.DISTANCE
      ? showMiles
        ? 'distanceMi'
        : 'distanceKm'
      : progressType === ProgressType.CALORIES
      ? 'caloriesTotal'
      : progressType === ProgressType.INTENSIVE_MINUTES
      ? 'intensiveMinutesTotal'
      : 'totalPercentage'*/
  const progressValueIndicator = 'sum'

  const customStepsGoal = isKatriito ? 6000 : null
  const goalValue: number | null = hideProgressGoals
    ? null
    : getGoalValue(progressType, customType, userDto, showMiles, customStepsGoal)
  const maxDomainValue = getMaxDomainValue(progressType, data, goalValue)
  const tickValues = getTickValues(goalValue, maxDomainValue)

  // @ts-ignore
  const yAxisTickLabelsFill: VictoryStringOrNumberCallback = ({ tick }) =>
    goalValue !== null && tick === goalValue ? darkGreen : 'rgba(36, 40, 78, 0.5)'

  // @ts-ignore
  const yAxisGridStroke: VictoryStringOrNumberCallback = ({ tick }) =>
    goalValue !== null && tick === goalValue ? darkGreen : '#ECEFF1'

  const responsiveStrokeWidthMultiplier = isMobile ? 1 : 2

  // @ts-ignore
  const yAxisGridStrokeWidth: VictoryStringOrNumberCallback = ({ tick }) =>
    goalValue !== null && tick === goalValue ? chartStrokeWidth * responsiveStrokeWidthMultiplier : chartStrokeWidth

  // @ts-ignore
  const yAxisGridStrokeDasharray: VictoryStringOrNumberCallback = ({ tick }) => {
    return goalValue !== null && tick === goalValue ? '5' : null
  }

  const yAxisTickFormat = (tickValue: any) => {
    // Hide y-axis tick values (gray numbers) that are too close to the goal value (green numbers).
    if (
      goalValue !== null &&
      tickValue !== goalValue &&
      Math.abs(tickValue - goalValue) < tickValue / ((1 / CLOSE_TICK_HIDE_PERCENTAGE) * 100)
    ) {
      return ''
    }
    if (isRtl) {
      //ticks = ticks.map((t) => convertNumbers2Arabic(String(t)))
      /*return progressType === ProgressType.FORMULA
        ? convertNumbers2Arabic(tickValue + '%')
        : progressType === ProgressType.CALORIES
        ? convertNumbers2Arabic(Math.round(tickValue))
        : convertNumbers2Arabic(tickValue)*/
      return progressType === ProgressType.FORMULA
        ? '٪' + tickValue
        : progressType === ProgressType.SLEEP_MIN
          ? convertMinutesToHoursAndMinutes(Math.round(tickValue))
          : progressType === ProgressType.CALORIES
            ? Math.round(tickValue)
            : tickValue >= 1000
              ? Math.round(tickValue)
              : tickValue
    }
    return progressType === ProgressType.FORMULA
      ? tickValue + '%'
      : progressType === ProgressType.SLEEP_MIN
        ? convertMinutesToHours(Math.round(tickValue))
        : progressType === ProgressType.CALORIES
          ? Math.round(tickValue)
          : tickValue >= 1000
            ? Math.round(tickValue / 1000) + 'k'
            : tickValue
  }

  const xAxisTickFormat = (d: Date) => {
    if (progressPeriod === ProgressPeriod.WEEK) {
      return dayjs(d).format('ddd')
    }
    return dayjs(d).format('D')
  }

  const updateIntegration = () => {
    props.setOnboardingStatusToActivityData()
  }

  const normalMainTabStyle = syncSleep ? { minWidth: '16.6%' } : { minWidth: '20%' }
  const disabledTabStyle = { opacity: 0.5, minWidth: syncSleep ? '16.6%' : '20%' }

  const barColor = (v: any) => {
    // Future-day bars double as the tooltip flyout fill, which carries white text.
    // darkGray (#CCCCCC) gave white 1.6:1; darkerGray (#707173) gives 4.9:1 (WCAG 1.4.3).
    if (!dayjs(v.datum.date).isBefore(new Date())) return darkerGray
    if (goalValue === null) return primaryBlue
    return v.datum.sum >= goalValue ? darkGreen : primaryBlue
  }

  const barStrokeWidth = (v: any) => {
    return dayjs(v.datum.date).isBefore(new Date()) ? 1 : 0
  }

  const width = 350
  const sectionWidth = width / data.length

  const tooltipLabel = ({ datum }: any) => {
    let number = datum[progressValueIndicator]
    let unit = ''
    if (progressType === ProgressType.FORMULA) {
      unit = isRtl ? '٪' : '%'
    } else if (progressType === ProgressType.STEPS) {
      unit = ' ' + t`steps`
    } else if (progressType === ProgressType.DISTANCE) {
      number = formatThousands(number, 2)
      unit = ' ' + (showMiles ? unitsCountLengthMileShort : unitsCountLengthKilometerShort)
    } else if (progressType === ProgressType.CALORIES) {
      unit = ' ' + unitsCountEnergyKilocalorieShort
    } else if (progressType === ProgressType.INTENSIVE_MINUTES) {
      unit = ' ' + unitsCountDurationMinuteShort
    } else if (progressType === ProgressType.SLEEP_MIN) {
      number = convertMinutesToHoursAndMinutes(number)
    }
    return isMobile && periodDays > 7
      ? `${number}${unit}\n${dayjs(datum.date).format('D. MMMM')}`
      : `${dayjs(datum.date).format('D. MMMM')} – ${number}${unit}`
  }

  const unitsLabel = getUnit(
    progressType,
    [ProgressType.DISTANCE, ProgressType.INTENSIVE_MINUTES].includes(progressType) ? customType : null,
    null,
  ) as string
  const isDistance = progressType === ProgressType.DISTANCE

  // @ts-ignore
  return (
    <Card>
      <NotificationsWrapper />
      <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: '16px' }} wrap="nowrap">
        <Grid item style={{ overflow: 'hidden' }}>
          <h1
            style={{ marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >{t`Progress`}</h1>
        </Grid>

        <Grid item style={{ marginLeft: isRtl ? '0' : 'auto' }}>
          <Grid container alignItems="center">
            <Hidden smDown>
              <Grid item>
                {
                  <div style={{ fontWeight: 600 }}>
                    <svg
                      style={{
                        width: '1rem',
                        height: '1rem',
                        marginTop: -2,
                        marginRight: isRtl ? 0 : '0.5rem',
                        marginLeft: isRtl ? '0.5rem' : 0,
                      }}
                      width="16"
                      height="16"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <rect y="1" width="12" height="11" rx="2" fill={primaryBlue} />
                      <rect x="2" width="2" height="2" rx="1" fill={primaryBlue} />
                      <rect x="2" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="2" y="7" width="2" height="2" rx="1" fill="white" />
                      <rect x="8" width="2" height="2" rx="1" fill={primaryBlue} />
                      <rect x="8" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="5" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="5" y="7" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    {getFormattedDates(startDate, endDate)}
                  </div>
                }
              </Grid>
            </Hidden>
            <Grid item>
              <div className={'hide-when-os-scale-large'}>
                <YumProgressPeriod progressPeriod={progressPeriod} setProgressPeriodDates={setProgressPeriodDates} />
              </div>
              <div className={'show-when-os-scale-large'}>
                <YumProgressPeriod
                  progressPeriod={progressPeriod}
                  setProgressPeriodDates={setProgressPeriodDates}
                  hidePeriod
                />
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div className={'show-when-os-scale-large'}>
        <YumProgressPeriod
          progressPeriod={progressPeriod}
          setProgressPeriodDates={setProgressPeriodDates}
          hideNav
          fullWidth
        />
      </div>

      <div className={'mobile-dates'} style={{ fontWeight: 600 }}>
        <svg
          style={{
            width: '1rem',
            height: '1rem',
            marginTop: -4,
            marginRight: isRtl ? 0 : '0.5rem',
            marginLeft: isRtl ? '0.5rem' : 0,
          }}
          width="16"
          height="16"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect y="1" width="12" height="11" rx="2" fill={primaryBlue} />
          <rect x="2" width="2" height="2" rx="1" fill={primaryBlue} />
          <rect x="2" y="4" width="2" height="2" rx="1" fill="white" />
          <rect x="2" y="7" width="2" height="2" rx="1" fill="white" />
          <rect x="8" width="2" height="2" rx="1" fill={primaryBlue} />
          <rect x="8" y="4" width="2" height="2" rx="1" fill="white" />
          <rect x="5" y="4" width="2" height="2" rx="1" fill="white" />
          <rect x="5" y="7" width="2" height="2" rx="1" fill="white" />
        </svg>
        {getFormattedDates(startDate, endDate)}
      </div>

      {!exerciseMinutesOnly && (
        <YumTabs
          className={
            syncSleep
              ? 'progress__tabs progress__tabs xxfull-width user-select-none'
              : 'progress__tabs user-select-none'
          }
          variant="scrollable"
          // "on" is the only mode whose arrows MUI v4 shows on phones; they render only when the
          // strip actually overflows, matching the challenge leaderboard tabs.
          scrollButtons="on"
          value={progressType}
          onChange={onProgressTypeChange}
        >
          <YumTab style={normalMainTabStyle} label={t`Steps`} value={ProgressType.STEPS} />
          <YumTab style={normalMainTabStyle} label={t`Distance`} value={ProgressType.DISTANCE} />
          <YumTab
            style={normalMainTabStyle}
            label={unitsDisplayDurationMinuteLong}
            value={ProgressType.INTENSIVE_MINUTES}
          />
          {syncSleep && <YumTab style={normalMainTabStyle} label={t`Sleep`} value={CustomType.SLEEP_MIN} />}
          <YumTab style={normalMainTabStyle} label={unitsDisplayDurationCalorieLong} value={ProgressType.CALORIES} />
          <YumTab
            label={t`Activity %`}
            value={ProgressType.FORMULA}
            disabled={!hasActivityIndex}
            style={!hasActivityIndex ? disabledTabStyle : normalMainTabStyle}
          />
        </YumTabs>
      )}

      {progressType === ProgressType.INTENSIVE_MINUTES && (
        <ProgressChips
          scroll
          ariaLabel={t`Activity type`}
          value={customType}
          onChange={(value) => props.setProgressCustomType(value)}
          options={[
            {
              label: t`Exercise minutes`,
              value: CustomType.EXERCISE_MIN,
              icon: typeFilterIcon('mixed_metabolic_cardio'),
            },
            ...(!exerciseMinutesOnly
              ? [
                  {
                    label: t`Intensive minutes`,
                    value: CustomType.INTENSIVE_MINUTES,
                    icon: typeFilterIcon('interval_training.high_intensity'),
                  },
                ]
              : []),
            ...(!exerciseMinutesOnly
              ? [{ label: t`Mindfulness`, value: CustomType.MINDFULNESS_MIN, icon: typeFilterIcon('meditation') }]
              : []),
            { label: t`Walking`, value: CustomType.WALKING_MIN, icon: typeFilterIcon('walking') },
            { label: t`Running`, value: CustomType.RUNNING_MIN, icon: typeFilterIcon('running') },
            { label: t`Cycling`, value: CustomType.CYCLING_MIN, icon: typeFilterIcon('cycling') },
            { label: t`Swimming`, value: CustomType.SWIMMING_MIN, icon: typeFilterIcon('swimming') },
            { label: t`Yoga`, value: CustomType.YOGA_MIN, icon: typeFilterIcon('yoga') },
            {
              label: t`Strength training`,
              value: CustomType.STRENGTH_TRAINING_MIN,
              icon: typeFilterIcon('strength_training'),
            },
          ]}
        />
      )}

      {progressType === ProgressType.DISTANCE && (
        <ProgressChips
          scroll
          ariaLabel={t`Activity type`}
          value={customType}
          onChange={(value) => props.setProgressCustomType(value)}
          options={[
            { label: t`Total`, value: showMiles ? CustomType.DISTANCE_MI : CustomType.DISTANCE_KM },
            {
              label: t`Walking`,
              value: showMiles ? CustomType.WALKING_MI : CustomType.WALKING_KM,
              icon: typeFilterIcon('walking'),
            },
            {
              label: t`Running`,
              value: showMiles ? CustomType.RUNNING_MI : CustomType.RUNNING_KM,
              icon: typeFilterIcon('running'),
            },
            {
              label: t`Cycling`,
              value: showMiles ? CustomType.CYCLING_MI : CustomType.CYCLING_KM,
              icon: typeFilterIcon('cycling'),
            },
            { label: t`Swimming`, value: CustomType.SWIMMING_M, icon: typeFilterIcon('swimming') },
          ]}
        />
      )}

      {!hasActivityIndex && progressType === ProgressType.CALORIES ? (
        <div className="activity-index-info">
          <span>
            {currentLang === Lang.EN || currentLang === Lang.ET
              ? t`If you want to know what your YuMuuv activity level is, you have to provide additional data.`
              : t`If you want to know what your YuMuuv activity level is, you have to provide additional data and connect a heart rate activity device.`}
          </span>
          {` `}
          <a href={undefined} onClick={updateIntegration}>{t`Start setup`}</a>
        </div>
      ) : (
        ''
      )}
      {progressType === ProgressType.DISTANCE && <YumInsufficientPermissionsWrapper special={true} />}
      {/*{distanceScopeMissing && progressType === ProgressType.DISTANCE && (
          <div className="activity-index-info">
            <div>{t`Distance`}</div>
            <span>{t`We seem to have insufficient permissions to sync with your ${serviceProvider} integration.`}</span>
            {` `}
            <a href={undefined} onClick={handleGoogleFitUnverified}>{t`Reauthorize`}</a>
          </div>
        )}*/}

      {!data.length ? (
        <Loader />
      ) : (
        <>
          <div className={'user-select-none'} aria-hidden="true">
            <svg style={{ height: 0 }}>
              <defs>
                <linearGradient id="myGradient">
                  <stop offset="0%" stopColor="red" />
                  <stop offset="25%" stopColor="orange" />
                  <stop offset="50%" stopColor="gold" />
                  <stop offset="75%" stopColor="yellow" />
                  <stop offset="100%" stopColor="green" />
                </linearGradient>
              </defs>
            </svg>
            <VictoryChart
              theme={VictoryTheme}
              height={isMobile ? 200 : 150}
              padding={{
                top: isMobile ? 20 : 10,
                bottom: 50,
                right: isRtl ? chartLeftPadding : chartRightPadding,
                left: isRtl ? chartRightPadding : chartLeftPadding,
              }}
              maxDomain={{ y: maxDomainValue }}
              containerComponent={<VictoryVoronoiContainer voronoiDimension="x" />}
              domainPadding={{ x: sectionWidth }}
            >
              <VictoryAxis
                dependentAxis
                offsetX={isRtl ? chartLeftPadding : chartRightPadding}
                orientation={isRtl ? 'left' : 'right'}
                tickFormat={yAxisTickFormat}
                style={{
                  tickLabels: {
                    width: 500,
                    fontSize: chartFontSize,
                    fill: yAxisTickLabelsFill,
                  },
                  axis: { stroke: 'none' },
                  ticks: { stroke: 'none' },
                  grid: {
                    stroke: yAxisGridStroke,
                    strokeWidth: yAxisGridStrokeWidth,
                    strokeDasharray: yAxisGridStrokeDasharray,
                  },
                }}
                tickValues={tickValues}
              />
              <VictoryAxis
                style={{
                  tickLabels: {
                    fontSize: chartFontSize,
                  },
                  grid: { stroke: 'none' },
                }}
                tickCount={isMobile && ProgressPeriod.MONTH === progressPeriod ? Math.ceil(periodDays / 4) : periodDays}
                tickFormat={xAxisTickFormat}
              />
              <VictoryBar
                dataComponent={
                  <Bar
                    style={{
                      stroke: barColor,
                      strokeWidth: barStrokeWidth,
                      fill: barColor,
                    }}
                  />
                }
                x="date"
                y={progressValueIndicator}
                data={data}
                cornerRadius={{ top: progressPeriod === ProgressPeriod.WEEK ? 2 : 1 }}
                labels={tooltipLabel}
                labelComponent={
                  <VictoryTooltip
                    cornerRadius={chartTooltipCornerRadius}
                    pointerWidth={chartTooltipPointerWidth}
                    pointerLength={chartTooltipPointerLength}
                    flyoutPadding={chartTooltipFlyoutPadding}
                    dy={chartTooltipDy}
                    flyoutStyle={{
                      stroke: barColor,
                      strokeWidth: chartTooltipStrokeWidth,
                      fill: barColor,
                    }}
                    style={{
                      fontSize: chartTooltipFontSize,
                      fill: 'white',
                      fontWeight: 400,
                      fontFamily: 'Helvetica Neue',
                    }}
                  />
                }
              />
            </VictoryChart>
          </div>

          <YumLogTable
            data={props.dailyDiary}
            valueGoal={goalValue}
            units={progressType === ProgressType.FORMULA ? t`Activity index` : unitsLabel}
            showDigits={isDistance}
            manualEntryEnabled={true}
            suffix={progressType === ProgressType.FORMULA ? '%' : ''}
            showTotalSum={true}
            showSeparateValues={true}
          />
        </>
      )}
    </Card>
  )
}
